import type { GetStaticProps } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'
import {
  getResearchAuthor,
  RESEARCH_METHODS_PATH,
  RESEARCH_TEAM_PATH,
} from '@dupe/common/research-report'
import Layout from '~/src/components/Layout'
import LottiePlayer from '~/src/components/LottiePlayer'
import {
  computeMinutesSaved,
  ResearchTimerBadge,
} from '~/src/components/research/ResearchTimerBadge'
import type { FeaturedResearchHubSummary } from '~/src/db/queryPublishedResearchHub'
import { createPublicUrl } from '~/src/lib/helpers'
import { serializeJsonLd } from '~/src/lib/reports/jsonLd'

// ---------------------------------------------------------------------------
// Static props: live, honest corpus stats (never hardcode these numbers)
// ---------------------------------------------------------------------------

interface ResearchMethodsPageProps {
  publishedReportCount: number | null
  categoryCount: number | null
  featuredHubs: FeaturedResearchHubSummary[]
  hubCount: number | null
  /** Cached at build/ISR time so the timer badge renders a stable, non-ticking value. */
  timeSavedMinutes: number
}

export const getStaticProps: GetStaticProps<
  ResearchMethodsPageProps
> = async () => {
  // Computed here (not on a client interval) so the value is cached by ISR and
  // identical on server + client — the badge stays static and never shifts.
  const timeSavedMinutes = computeMinutesSaved()
  try {
    const [{ queryPublishedResearchReportCategories }, hubQueries] =
      await Promise.all([
        import('~/src/db/queryPublishedResearchReport'),
        import('~/src/db/queryPublishedResearchHub'),
      ])
    const [categories, featuredHubs, hubCount] = await Promise.all([
      queryPublishedResearchReportCategories(),
      hubQueries.queryFeaturedResearchHubs(3),
      hubQueries.queryPublishedResearchHubCount(),
    ])
    return {
      props: {
        publishedReportCount: categories.reduce(
          (sum, category) => sum + category.reportCount,
          0
        ),
        categoryCount: categories.length,
        featuredHubs,
        hubCount,
        timeSavedMinutes,
      },
      revalidate: 3600,
    }
  } catch {
    // Render without stats rather than failing the build: the page must
    // never show a number we could not actually compute.
    return {
      props: {
        publishedReportCount: null,
        categoryCount: null,
        featuredHubs: [],
        hubCount: null,
        timeSavedMinutes,
      },
      revalidate: 300,
    }
  }
}

// ---------------------------------------------------------------------------
// Pipeline content
// ---------------------------------------------------------------------------

interface PipelineSection {
  id: string
  step: string
  title: string
  animation: string
  animationLabel: string
  dataIn: string
  dataOut: string
  paragraphs: string[]
}

const PIPELINE: PipelineSection[] = [
  {
    id: 'understand',
    step: 'Step 01',
    title: 'Every report starts with a question',
    animation: '/lottie/research-methods/intent.json',
    animationLabel:
      'A search query flowing into a buyer profile with shopping priorities',
    dataIn: 'A search query',
    dataOut: 'A buyer profile and research plan',
    paragraphs: [
      'A report begins the way your shopping does: with a search. Before any research runs, we work out who is actually asking. A shopper hunting the lowest defensible price behaves differently from one who wants the closest match to a designer original, or one who only trusts what other owners say.',
      'That buyer profile decides everything downstream: which products are even candidates, which criteria matter for this category, and what evidence would settle the question. It becomes the research plan the rest of the pipeline executes against.',
    ],
  },
  {
    id: 'gather',
    step: 'Step 02',
    title: 'AI agents sweep the open web',
    animation: '/lottie/research-methods/sources.json',
    animationLabel: 'Multiple source types streaming evidence into one store',
    dataIn: 'The research plan',
    dataOut: 'Evidence, kept with its source',
    paragraphs: [
      'Our research agents read what a careful human shopper would read (retailer product pages, current pricing, manufacturer specs, owner reviews, YouTube reviews, and Reddit threads), but at a scale no human team could: typically hundreds of sources for a single report.',
      'Nothing is collected as a loose fact. Every claim is stored with the source it came from, which is why each published report can list its sources at the bottom. If we cannot point at where something came from, it does not get used.',
    ],
  },
  {
    id: 'screen',
    step: 'Step 03',
    title: 'Evidence is screened before it counts',
    animation: '/lottie/research-methods/verify.json',
    animationLabel:
      'Evidence passing through a screen, with weak evidence dropping out',
    dataIn: 'Raw gathered evidence',
    dataOut: 'A vetted candidate set',
    paragraphs: [
      'Raw web data is messy: duplicate listings, outdated prices, reviews about a different variant, products that look right in a thumbnail but fail the category definition. Before anything is scored, the evidence is screened: deduplicated, checked against the category’s hard constraints, and stripped of claims that cannot be tied to a source.',
      'Products are cut here too. A candidate without enough independent evidence behind it does not advance, no matter how good it looks. We would rather publish a shorter report than pad one with products we cannot back up.',
    ],
  },
  {
    id: 'score',
    step: 'Step 04',
    title: 'Candidates are scored on category criteria',
    animation: '/lottie/research-methods/score.json',
    animationLabel:
      'Evidence flowing into a scoring panel where ranked bars grow',
    dataIn: 'Vetted candidates',
    dataOut: 'A ranked shortlist',
    paragraphs: [
      'Every surviving candidate is scored against the criteria that actually matter for its category: things like visual fidelity to the original, the real price difference, what owners report after months of use, and how materials and specs compare. The criteria change by category; a sofa is not judged like a serum.',
      'We publish the criteria, not just the verdict: each report’s "How we picked" section states what its picks were scored against, so you can disagree with our weighting instead of having to guess at it.',
    ],
  },
  {
    id: 'review',
    step: 'Step 05',
    title: 'Humans make the final call',
    animation: '/lottie/research-methods/review.json',
    animationLabel:
      'Ranked candidates reaching a human reviewer who approves the top pick',
    dataIn: 'The ranked shortlist',
    dataOut: 'Approved top picks',
    paragraphs: [
      'The Dupe team reviews the findings and selects the top picks before a report is published. Agents are good at coverage; people are accountable for judgment. A pick that contradicts its own evidence, a price that looks wrong, a product that should not have survived screening: this is where it gets caught.',
      'The same accountability applies after publication: when readers or our own checks surface an error, we correct the report or take it down. The people behind this are on the research team page, not hidden behind invented bylines.',
    ],
  },
  {
    id: 'publish',
    step: 'Step 06',
    title: 'We monitor the reports for the latest products and developments',
    animation: '/lottie/research-methods/publish.json',
    animationLabel:
      'An approved report becoming a published page, with feedback flowing back into the pipeline',
    dataIn: 'Approved top picks',
    dataOut: 'A live report, and corrections back into the pipeline',
    paragraphs: [
      'A published report shows its work: the sources behind it, the date it was last updated, the criteria it used, and a disclosure of how we make money. Buy links may earn us a commission, but reports are researched and ranked before monetization is considered; a product never ranks higher because it pays more.',
      'Publication is not the end of the pipeline. Reader feedback and our own checks flow back to the start: reports get corrected, re-researched, or unpublished when the evidence changes. A recommendation is only as good as its maintenance.',
    ],
  },
]

// ---------------------------------------------------------------------------
// Theme-aware Lottie: colors are baked into each JSON, so dark mode loads a
// pre-built dark variant (white cards -> medium grey) generated alongside the
// light file. Renders client-side only (LottiePlayer is ssr:false), so the
// theme swap never causes a hydration mismatch.
// ---------------------------------------------------------------------------

function MethodologyLottie({ src, label }: { src: string; label: string }) {
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const finalSrc =
    mounted && resolvedTheme === 'dark'
      ? src.replace(/\.json$/, '.dark.json')
      : src
  return (
    <LottiePlayer
      src={finalSrc}
      autoplay
      loop
      aria-label={label}
      className='aspect-[2/1] w-full'
    />
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ResearchMethodsPage({
  publishedReportCount,
  categoryCount,
  featuredHubs,
  hubCount,
  timeSavedMinutes,
}: ResearchMethodsPageProps) {
  const author = getResearchAuthor()
  const canonicalUrl = createPublicUrl(RESEARCH_METHODS_PATH)

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'How Dupe Research Works',
    url: canonicalUrl,
    description:
      'The Dupe research pipeline, step by step: AI agents gather and screen sourced evidence, candidates are scored on stated category criteria, and the Dupe team reviews the findings and selects the top picks.',
    about: {
      '@type': 'Organization',
      name: author.name,
      url: author.url,
      description: author.bio,
    },
  }

  return (
    <Layout
      nextSeo={{
        title: 'How Dupe Research Works: Our Methodology',
        description:
          'Six steps from a search query to a published report: AI agents gather and screen sourced evidence, candidates are scored on stated criteria, and the Dupe team selects the top picks.',
        canonical: canonicalUrl,
        openGraph: {
          title: 'How Dupe Research Works',
          description:
            'The full Dupe research pipeline: sourced evidence, stated criteria, human-reviewed picks.',
          type: 'website',
          url: canonicalUrl,
        },
      }}
      fullWidth
      hideBorders
      minHeight
      className='pb-24 pt-10 md:pt-16'
    >
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(jsonLd) }}
        />
      </Head>

      {/* Hero */}
      <header className='mx-auto flex w-full max-w-6xl flex-col items-start gap-5 px-4 md:px-10'>
        <nav
          aria-label='Breadcrumb'
          className='text-sm text-foreground-tertiary'
        >
          <Link href='/reports' className='hover:text-foreground-secondary'>
            Home
          </Link>
          <span aria-hidden='true' className='mx-2'>
            /
          </span>
          <span className='text-foreground-secondary'>Research methods</span>
        </nav>
        <span className='rounded-full border border-border bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wide text-foreground-secondary dark:bg-surface-soft'>
          Dupe Deep Research Methodology
        </span>
        <h1 className='max-w-5xl text-pretty font-display text-[30px] font-bold leading-[1.15] tracking-[-1px] text-foreground md:text-4xl md:leading-[1.1] lg:text-5xl lg:leading-[1.08] xl:text-[52px]'>
          Here&apos;s how Dupe does Deep Research
        </h1>
        <ResearchTimerBadge
          minutesSaved={timeSavedMinutes}
          className='items-start'
        />
        <p className='max-w-2xl text-base leading-7 text-foreground-secondary md:text-lg'>
          Every Dupe report runs through the same six-step pipeline: AI agents
          gather and screen evidence from across the web, candidates are scored
          on criteria we publish, and the Dupe team reviews the findings and
          selects the top picks. This page walks through each step and what
          flows between them.
        </p>
        <div className='flex flex-wrap items-center gap-x-8 gap-y-4'>
          {publishedReportCount != null &&
            categoryCount != null &&
            publishedReportCount > 0 && (
              <p className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground'>
                <span className='font-medium text-foreground'>
                  {publishedReportCount.toLocaleString('en-US')} Consumer
                  Reports
                </span>
                <span aria-hidden='true'>·</span>
                <span>
                  {categoryCount.toLocaleString('en-US')} product categories
                </span>
                <span aria-hidden='true'>·</span>
                <Link
                  href={RESEARCH_TEAM_PATH}
                  className='font-medium text-info-700 underline underline-offset-4 transition-colors hover:text-info-700/80'
                >
                  Meet the research team
                </Link>
              </p>
            )}
        </div>
      </header>

      {/* Pipeline at a glance */}
      <section
        aria-label='The pipeline at a glance'
        className='mx-auto mt-14 w-full max-w-6xl px-4 md:mt-20 md:px-10'
      >
        <div className='flex flex-col gap-3'>
          <p className='text-sm font-semibold uppercase tracking-wide text-foreground-tertiary'>
            At a glance
          </p>
          <h2 className='font-display text-2xl font-bold leading-8 text-foreground md:text-3xl'>
            Step by step methodology breakdown
          </h2>
          <p className='max-w-2xl text-sm leading-6 text-foreground-secondary md:text-base'>
            Data only moves forward when it has earned it. A query becomes a
            buyer profile, the profile directs the sweep, the sweep produces
            sourced evidence, the evidence is screened, survivors are scored,
            and people approve what gets published. Each section below shows
            what flows in and out of that step.
          </p>
          <p className='max-w-2xl text-sm leading-6 text-foreground-secondary md:text-base'>
            The exact scoring weights and tooling are ours; everything a reader
            needs to interrogate a report (the sources, the criteria, the dates,
            the money) is on the report itself.
          </p>
        </div>

        {/* Six-step flow: each card links to its section and shows what
            flows in and out. Numbered 01 to 06 so the chain reads in order;
            each card's In to Out arrow shows what that step transforms. */}
        <ol className='mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:mt-10'>
          {PIPELINE.map((section, index) => (
            <li key={section.id} className='group flex flex-col items-center'>
              <Link
                href={`#${section.id}`}
                className='flex w-full flex-1 flex-col gap-3 rounded-2xl border border-border bg-white p-5 transition-colors hover:border-info-500/60 hover:bg-surface-muted dark:bg-surface-soft dark:hover:bg-surface-alt'
              >
                <div className='flex items-center gap-3'>
                  <span
                    aria-hidden='true'
                    className='flex size-9 shrink-0 items-center justify-center rounded-full border border-info-500/40 bg-info-500/10 font-display text-sm font-bold text-info-700'
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className='text-xs font-semibold uppercase tracking-wide text-foreground-tertiary'>
                    {section.step}
                  </span>
                </div>
                <span className='font-display text-base font-bold leading-6 text-foreground group-hover:underline group-hover:decoration-border group-hover:underline-offset-4'>
                  {section.title}
                </span>
                <span className='mt-auto flex flex-wrap items-center gap-1.5 text-xs leading-5'>
                  <span className='text-foreground-secondary'>
                    {section.dataIn}
                  </span>
                  <span
                    aria-hidden='true'
                    className='font-semibold text-info-700'
                  >
                    →
                  </span>
                  <span className='font-medium text-foreground'>
                    {section.dataOut}
                  </span>
                </span>
              </Link>
              {index < PIPELINE.length - 1 && (
                <span
                  aria-hidden='true'
                  className='py-1 text-base font-semibold leading-none text-foreground-tertiary sm:hidden'
                >
                  ↓
                </span>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* Pipeline sections */}
      <div className='mx-auto mt-16 flex w-full max-w-6xl flex-col gap-14 px-4 md:mt-24 md:gap-16 md:px-10 [&>section+section]:border-t [&>section+section]:border-border [&>section+section]:pt-14 [&>section]:scroll-mt-14'>
        {PIPELINE.map((section, index) => {
          const animationFirst = index % 2 === 1
          return (
            <section
              key={section.id}
              id={section.id}
              aria-label={section.title}
            >
              <div className='grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-12'>
                <div
                  className={
                    animationFirst
                      ? 'flex flex-col gap-4 md:order-2'
                      : 'flex flex-col gap-4'
                  }
                >
                  <p className='text-sm font-semibold uppercase tracking-wide text-foreground-tertiary'>
                    {section.step}
                  </p>
                  <h2 className='font-display text-2xl font-bold leading-8 text-foreground md:text-3xl'>
                    {section.title}
                  </h2>
                  {section.paragraphs.map((paragraph) => (
                    <p
                      key={paragraph.slice(0, 32)}
                      className='text-base leading-7 text-foreground-secondary'
                    >
                      {paragraph}
                    </p>
                  ))}
                  <dl className='mt-2 flex flex-wrap gap-2 text-xs'>
                    <div className='flex items-center gap-1.5 rounded-full border border-border bg-accent px-3 py-1.5 dark:bg-surface-soft'>
                      <dt className='font-semibold uppercase tracking-wide text-foreground-tertiary'>
                        In
                      </dt>
                      <dd className='text-foreground-secondary'>
                        {section.dataIn}
                      </dd>
                    </div>
                    <div className='flex items-center gap-1.5 rounded-full border border-info-500/40 bg-info-500/10 px-3 py-1.5'>
                      <dt className='font-semibold uppercase tracking-wide text-info-700'>
                        Out
                      </dt>
                      <dd className='text-foreground-secondary'>
                        {section.dataOut}
                      </dd>
                    </div>
                  </dl>
                </div>
                <div
                  className={
                    animationFirst
                      ? 'overflow-hidden rounded-3xl bg-surface-cyan ring-1 ring-border dark:bg-surface-soft md:order-1'
                      : 'overflow-hidden rounded-3xl bg-surface-cyan ring-1 ring-border dark:bg-surface-soft'
                  }
                >
                  <MethodologyLottie
                    src={section.animation}
                    label={section.animationLabel}
                  />
                </div>
              </div>
            </section>
          )
        })}
      </div>

      {/* What the pipeline produces */}
      <section
        aria-label='What the pipeline produces'
        className='mx-auto mt-20 w-full max-w-6xl px-4 md:mt-28 md:px-10'
      >
        <div className='flex flex-col gap-3'>
          <p className='text-sm font-semibold uppercase tracking-wide text-foreground-tertiary'>
            The output
          </p>
          <h2 className='font-display text-2xl font-bold leading-8 text-foreground md:text-3xl'>
            What the pipeline produces
          </h2>
          <p className='max-w-2xl text-sm leading-6 text-foreground-secondary md:text-base'>
            The pipeline publishes into research hubs. Each topic gets two: a
            transactional Top Picks page for readers ready to choose, and an
            informational Guide for readers still working out what matters. Both
            are built on the same underlying reports and the same sourced
            evidence described above.
          </p>
        </div>
        {featuredHubs.length > 0 ? (
          <div className='mt-8 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5'>
            {featuredHubs.map((hub) => (
              <Link
                key={hub.slug}
                href={hub.path}
                className='group flex flex-col overflow-hidden rounded-2xl border border-border bg-background transition-colors hover:border-info-500/60 hover:bg-accent dark:hover:bg-surface-soft'
              >
                {hub.heroImageUrl && (
                  // eslint-disable-next-line @next/next/no-img-element -- hub
                  // hero images are arbitrary external URLs; avoids per-domain
                  // next/image allowlisting on this low-traffic editorial page.
                  <img
                    src={hub.heroImageUrl}
                    alt={hub.heroImageAlt || hub.title}
                    loading='lazy'
                    className='aspect-[16/9] w-full object-cover'
                  />
                )}
                <div className='flex flex-1 flex-col gap-3 p-6'>
                  <span
                    className={
                      hub.hubKind === 'transactional'
                        ? 'self-start rounded-full border border-info-500/40 bg-info-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-info-700'
                        : 'self-start rounded-full border border-border bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wide text-foreground-secondary dark:bg-surface-soft'
                    }
                  >
                    {hub.hubKind === 'transactional'
                      ? "Dupe's Top Picks"
                      : 'Dupe Guide'}
                  </span>
                  <span className='font-display text-base font-bold leading-snug text-foreground group-hover:underline group-hover:decoration-border group-hover:underline-offset-4 md:text-lg md:leading-6'>
                    {hub.title}
                  </span>
                  {hub.description && (
                    <span className='line-clamp-3 text-sm leading-6 text-foreground-secondary'>
                      {hub.description}
                    </span>
                  )}
                  <div className='mt-auto flex flex-col gap-2 pt-2'>
                    {hub.reportCount > 0 && (
                      <span className='text-xs text-foreground-tertiary'>
                        Built on {hub.reportCount}{' '}
                        {hub.reportCount === 1 ? 'report' : 'reports'}
                      </span>
                    )}
                    {/* Whole card is the link; this is a button-styled span
                        (not a nested anchor) so the markup stays valid. */}
                    <span className='inline-flex items-center justify-center self-start rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition-opacity group-hover:opacity-90'>
                      {hub.hubKind === 'transactional'
                        ? 'Open Top Picks'
                        : 'Open Guide'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
            {hubCount != null && hubCount > featuredHubs.length && (
              <Link
                href='/reports'
                className='group flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-background p-6 text-center transition-colors hover:border-info-500/60 hover:bg-accent dark:hover:bg-surface-soft'
              >
                <span className='font-display text-2xl font-bold text-foreground'>
                  +{(hubCount - featuredHubs.length).toLocaleString('en-US')}{' '}
                  more
                </span>
                <span className='text-sm text-foreground-secondary'>
                  Browse every research hub
                </span>
              </Link>
            )}
          </div>
        ) : (
          <div className='mt-8'>
            <Link
              href='/reports'
              className='inline-flex items-center rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent dark:hover:bg-surface-soft'
            >
              Browse the research
            </Link>
          </div>
        )}
      </section>

      {/* Closing: people + money + CTA */}
      <section
        aria-label='Accountability'
        className='mx-auto mt-16 w-full max-w-6xl px-4 md:mt-24 md:px-10'
      >
        <div className='flex flex-col gap-6 rounded-3xl border border-border bg-accent p-6 dark:bg-surface-soft md:p-10'>
          <h2 className='font-display text-2xl font-bold leading-8 text-foreground md:text-3xl'>
            Get to know the Dupe Team
          </h2>
          <p className='max-w-2xl text-sm leading-6 text-foreground-secondary md:text-base'>
            Reports are published under the Dupe Research Team: AI-assisted,
            human-reviewed, never under invented author names. When you buy
            through links on a report we may earn a commission at no extra cost
            to you; reports are researched and ranked before monetization is
            considered, and a product never ranks higher because it pays more.
          </p>
          <div className='flex flex-wrap gap-3'>
            <Link
              href={RESEARCH_TEAM_PATH}
              className='inline-flex items-center rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-90'
            >
              Learn more about the Deep Research Team
            </Link>
            <Link
              href='/reports'
              className='inline-flex items-center rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent dark:hover:bg-surface-alt'
            >
              Browse the research
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  )
}
